/**
 * Whether anything recorded at this register can reach Manage at all.
 *
 * On the header rather than only in the `/mappings` report, because the person about to spend an
 * afternoon on a register's rules is the one who needs to know the work will not reach its
 * consumer. It is a statement of fact and not a warning to act on here — the decision is somebody
 * else's and lives on the mapping screen, which this links to.
 *
 * Silent when every matter is mapped. A badge that is always present stops being read.
 */
import { Link } from 'react-router';
import { Pill } from '~/ui/Pill';
import { routes } from '~/lib/routes';
import type { MatterSummary } from '~/api/schema';

export function ManageMappingBadge({ matters }: { matters: readonly MatterSummary[] }) {
  const unmapped = matters.filter((matter) => matter.manageMapping === 'unmapped');
  const none = matters.filter((matter) => matter.manageMapping === 'no_counterpart');

  if (unmapped.length === 0 && none.length === 0) return null;

  // The two are kept apart for the same reason the mapping screen keeps them apart: one is work
  // and the other is a finding that will never move.
  const [tone, label] =
    unmapped.length > 0
      ? (['quest', 'Manage counterpart not recorded'] as const)
      : (['issue', 'No Manage counterpart'] as const);

  return (
    <Link to={routes.mappings()} aria-label={`${label} — see the Manage mapping report`}>
      <Pill tone={tone} size="mini">
        {label}
      </Pill>
    </Link>
  );
}
